import { useEffect, useMemo, useState } from 'react'
import {
  Plus, Trash2, Download, Sparkles, Clock, DollarSign, TrendingUp, TrendingDown,
  CheckCircle2, AlertTriangle, FileText, Archive, ArchiveRestore, Calculator,
} from 'lucide-react'
import {
  type Project, type TimeEntry,
  statsForProject, portfolioStats, entriesToCsv, checkQuote,
} from './lib/engine'
import { aiPricingInsights } from './lib/ai-fallback'
import { SettingsButton } from './lib/SettingsButton'

const PROJECTS_KEY = 'tl_projects'
const ENTRIES_KEY = 'tl_entries'

function load<T>(key: string, fallback: T): T {
  try { return JSON.parse(localStorage.getItem(key) ?? '') as T } catch { return fallback }
}
function save<T>(key: string, value: T) { localStorage.setItem(key, JSON.stringify(value)) }
function uid() { return Math.random().toString(36).slice(2, 10) + Date.now().toString(36) }
function today() { return new Date().toISOString().slice(0, 10) }
function money(n: number) { return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }) }

export default function App() {
  const [projects, setProjects] = useState<Project[]>(() => load(PROJECTS_KEY, [] as Project[]))
  const [entries, setEntries] = useState<TimeEntry[]>(() => load(ENTRIES_KEY, [] as TimeEntry[]))
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null)
  const [aiOutput, setAiOutput] = useState('')
  const [aiLoading, setAiLoading] = useState(false)
  const [quoteCheckOpen, setQuoteCheckOpen] = useState(false)
  const [quotePrice, setQuotePrice] = useState('')
  const [quoteHours, setQuoteHours] = useState('')

  useEffect(() => { save(PROJECTS_KEY, projects) }, [projects])
  useEffect(() => { save(ENTRIES_KEY, entries) }, [entries])

  const portfolio = useMemo(() => portfolioStats(projects, entries), [projects, entries])
  const projectStats = useMemo(
    () => projects.filter(p => p.status !== 'archived').map(p => statsForProject(p, entries)),
    [projects, entries]
  )
  const selected = selectedProjectId
    ? projects.find(p => p.id === selectedProjectId) ?? null
    : null
  const selectedEntries = selectedProjectId
    ? entries.filter(e => e.projectId === selectedProjectId).sort((a, b) => b.date.localeCompare(a.date))
    : []
  const selectedStats = selected ? statsForProject(selected, entries) : null

  // === Project actions ===
  const addProject = (p: Omit<Project, 'id' | 'createdAt' | 'status'>) => {
    const project: Project = { ...p, id: uid(), status: 'active', createdAt: today() }
    setProjects(prev => [project, ...prev])
    setSelectedProjectId(project.id)
  }
  const updateProject = (id: string, patch: Partial<Project>) => {
    setProjects(prev => prev.map(p => p.id === id ? { ...p, ...patch } : p))
  }
  const deleteProject = (id: string) => {
    if (!confirm('Delete this project and all its time entries? This cannot be undone.')) return
    setProjects(prev => prev.filter(p => p.id !== id))
    setEntries(prev => prev.filter(e => e.projectId !== id))
    if (selectedProjectId === id) setSelectedProjectId(null)
  }

  // === Time entry actions ===
  const addEntry = (entry: Omit<TimeEntry, 'id'>) => {
    setEntries(prev => [{ ...entry, id: uid() }, ...prev])
  }
  const deleteEntry = (id: string) => {
    setEntries(prev => prev.filter(e => e.id !== id))
  }

  // === Export ===
  const exportCsv = () => {
    const csv = entriesToCsv(projects, entries)
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `timeledger-${today()}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  // === AI ===
  const runAi = async () => {
    setAiLoading(true)
    setAiOutput('')
    let text = ''
    await aiPricingInsights(portfolio, projectStats, (chunk) => {
      text += chunk
      setAiOutput(text)
    })
    setAiLoading(false)
  }

  // === Quote check ===
  const quoteResult = useMemo(() => {
    const p = parseFloat(quotePrice)
    const h = parseFloat(quoteHours)
    if (!isFinite(p) || !isFinite(h) || p <= 0 || h <= 0) return null
    return checkQuote(p, h, projects, entries)
  }, [quotePrice, quoteHours, projects, entries])

  return (
    <div className="min-h-screen bg-[var(--color-navy-950)] text-[var(--color-ink)]">
      <header className="border-b border-white/10 bg-[var(--color-navy-900)] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[var(--color-gold)] flex items-center justify-center text-[var(--color-navy-900)] font-bold text-lg font-mono">T</div>
            <div>
              <h1 className="text-lg font-semibold tracking-tight text-[var(--color-ink)]">
                Time<span className="text-[var(--color-gold)]">Ledger</span>
              </h1>
              <p className="text-xs text-[var(--color-ink-muted)]">Time tracking calibrated for fixed-rate quoting</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setQuoteCheckOpen(true)}
              className="px-3 py-1.5 text-xs rounded-md bg-[var(--color-navy-700)] hover:bg-[var(--color-navy-600)] border border-white/10 flex items-center gap-1.5 transition-colors text-[var(--color-ink-muted)] hover:text-[var(--color-gold)]">
              <Calculator className="w-3.5 h-3.5" /> Quote check
            </button>
            <button onClick={exportCsv}
              className="px-3 py-1.5 text-xs rounded-md bg-[var(--color-navy-700)] hover:bg-[var(--color-navy-600)] border border-white/10 flex items-center gap-1.5 transition-colors text-[var(--color-ink-muted)] hover:text-[var(--color-gold)]">
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
            <SettingsButton className="px-3 py-1.5 text-xs rounded-md bg-[var(--color-navy-700)] hover:bg-[var(--color-navy-600)] border border-white/10 inline-flex items-center gap-1.5 transition-colors text-[var(--color-ink-muted)] hover:text-[var(--color-gold)]" />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* LEFT: portfolio + project list */}
        <aside className="lg:col-span-4 space-y-4">
          <PortfolioCard stats={portfolio} onAiClick={runAi} aiLoading={aiLoading} />

          <div className="bg-[var(--color-navy-800)] rounded-xl border border-white/10 p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-[var(--color-gold)]">Projects ({projects.length})</h2>
            </div>
            <NewProjectForm onAdd={addProject} />
            <div className="mt-3 space-y-2 max-h-[60vh] overflow-y-auto">
              {projectStats.length === 0 && (
                <p className="text-xs text-[var(--color-ink-muted)] italic">
                  Add a project to start tracking. The math kicks in the moment you log your first hour.
                </p>
              )}
              {projectStats.map(s => (
                <ProjectListItem key={s.projectId}
                  stats={s}
                  selected={s.projectId === selectedProjectId}
                  onSelect={() => setSelectedProjectId(s.projectId)} />
              ))}
            </div>
          </div>
        </aside>

        {/* RIGHT: selected project detail */}
        <section className="lg:col-span-8 space-y-4">
          {!selected && (
            <EmptyState />
          )}
          {selected && selectedStats && (
            <>
              <ProjectDetailCard
                project={selected}
                stats={selectedStats}
                onStatusChange={(status) => updateProject(selected.id, { status })}
                onDelete={() => deleteProject(selected.id)} />

              <div className="bg-[var(--color-navy-800)] rounded-xl border border-white/10 p-4">
                <h3 className="text-sm font-semibold text-[var(--color-gold)] mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4" /> Log time
                </h3>
                <NewEntryForm projectId={selected.id} onAdd={addEntry} />
              </div>

              <div className="bg-[var(--color-navy-800)] rounded-xl border border-white/10 p-4">
                <h3 className="text-sm font-semibold text-[var(--color-gold)] mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" /> Entries ({selectedEntries.length})
                </h3>
                <EntryList entries={selectedEntries} onDelete={deleteEntry} />
              </div>
            </>
          )}

          {aiOutput && (
            <div className="bg-[var(--color-navy-800)] rounded-xl border border-[var(--color-gold)]/30 p-4">
              <h3 className="text-sm font-semibold text-[var(--color-gold)] mb-3 flex items-center gap-2">
                <Sparkles className="w-4 h-4" /> AI pricing insights
                {aiLoading && <span className="text-xs text-[var(--color-ink-muted)] animate-pulse">streaming…</span>}
              </h3>
              <div className="text-sm text-[var(--color-ink)] whitespace-pre-wrap leading-relaxed font-mono">
                {aiOutput}
              </div>
            </div>
          )}
        </section>
      </main>

      {quoteCheckOpen && (
        <QuoteCheckModal
          price={quotePrice} setPrice={setQuotePrice}
          hours={quoteHours} setHours={setQuoteHours}
          result={quoteResult}
          onClose={() => setQuoteCheckOpen(false)} />
      )}
    </div>
  )
}

// ============================================================================
// Sub-components
// ============================================================================

function PortfolioCard({ stats, onAiClick, aiLoading }: {
  stats: ReturnType<typeof portfolioStats>,
  onAiClick: () => void, aiLoading: boolean
}) {
  const noData = stats.projectCount === 0
  return (
    <div className="bg-gradient-to-br from-[var(--color-navy-800)] to-[var(--color-navy-700)] rounded-xl border border-[var(--color-gold)]/20 p-4">
      <h2 className="text-sm font-semibold text-[var(--color-gold)] mb-3">Portfolio</h2>
      {noData ? (
        <p className="text-xs text-[var(--color-ink-muted)] italic">Numbers light up once you add your first project.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <Stat label="Blended rate" value={`$${stats.blendedHourlyRate.toFixed(0)}/hr`} accent />
            <Stat label="Total quoted" value={money(stats.totalQuoted)} />
            <Stat label="Actual hours" value={stats.totalActualHours.toFixed(1)} />
            <Stat label="Est. hours" value={stats.totalEstimatedHours.toFixed(0)} />
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="flex items-center gap-1.5 text-emerald-400"><CheckCircle2 className="w-3 h-3" /> On track: {stats.onTrackCount}</div>
            <div className="flex items-center gap-1.5 text-red-400"><AlertTriangle className="w-3 h-3" /> Over: {stats.overBudgetCount}</div>
          </div>
          <button onClick={onAiClick} disabled={aiLoading || stats.totalActualHours === 0}
            className="mt-4 w-full px-3 py-2 rounded-lg bg-[var(--color-gold)] text-[var(--color-navy-900)] font-semibold text-xs hover:bg-[var(--color-gold-bright)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" /> {aiLoading ? 'Analysing…' : 'AI pricing insights'}
          </button>
        </>
      )}
    </div>
  )
}

function Stat({ label, value, accent = false }: { label: string, value: string, accent?: boolean }) {
  return (
    <div className="bg-[var(--color-navy-900)] rounded-lg p-2.5 border border-white/5">
      <div className="text-[10px] uppercase tracking-wider text-[var(--color-ink-muted)] mb-1">{label}</div>
      <div className={`text-base font-semibold font-mono ${accent ? 'text-[var(--color-gold)]' : 'text-[var(--color-ink)]'}`}>{value}</div>
    </div>
  )
}

function NewProjectForm({ onAdd }: { onAdd: (p: Omit<Project, 'id' | 'createdAt' | 'status'>) => void }) {
  const [name, setName] = useState('')
  const [client, setClient] = useState('')
  const [price, setPrice] = useState('')
  const [hours, setHours] = useState('')
  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    const p = parseFloat(price), h = parseFloat(hours)
    if (!name.trim() || !isFinite(p) || !isFinite(h) || p <= 0 || h <= 0) return
    onAdd({ name: name.trim(), client: client.trim() || '—', quotedPrice: p, estimatedHours: h })
    setName(''); setClient(''); setPrice(''); setHours('')
  }
  return (
    <form onSubmit={submit} className="space-y-2">
      <input value={name} onChange={e => setName(e.target.value)} placeholder="Project name"
        className="w-full bg-[var(--color-navy-900)] border border-white/10 rounded-md px-2.5 py-1.5 text-sm placeholder:text-[var(--color-ink-muted)]/60 focus:outline-none focus:border-[var(--color-gold)]/50" />
      <input value={client} onChange={e => setClient(e.target.value)} placeholder="Client (optional)"
        className="w-full bg-[var(--color-navy-900)] border border-white/10 rounded-md px-2.5 py-1.5 text-sm placeholder:text-[var(--color-ink-muted)]/60 focus:outline-none focus:border-[var(--color-gold)]/50" />
      <div className="grid grid-cols-2 gap-2">
        <input value={price} onChange={e => setPrice(e.target.value)} placeholder="$ quoted" type="number" min="0" step="any"
          className="w-full bg-[var(--color-navy-900)] border border-white/10 rounded-md px-2.5 py-1.5 text-sm font-mono placeholder:text-[var(--color-ink-muted)]/60 focus:outline-none focus:border-[var(--color-gold)]/50" />
        <input value={hours} onChange={e => setHours(e.target.value)} placeholder="est. hrs" type="number" min="0" step="any"
          className="w-full bg-[var(--color-navy-900)] border border-white/10 rounded-md px-2.5 py-1.5 text-sm font-mono placeholder:text-[var(--color-ink-muted)]/60 focus:outline-none focus:border-[var(--color-gold)]/50" />
      </div>
      <button type="submit" className="w-full px-2.5 py-1.5 rounded-md bg-[var(--color-gold)] text-[var(--color-navy-900)] text-xs font-semibold hover:bg-[var(--color-gold-bright)] transition-colors flex items-center justify-center gap-1.5">
        <Plus className="w-3.5 h-3.5" /> Add project
      </button>
    </form>
  )
}

function ProjectListItem({ stats, selected, onSelect }: {
  stats: ReturnType<typeof statsForProject>, selected: boolean, onSelect: () => void
}) {
  const healthBg = stats.health === 'red' ? 'bg-red-500/20 text-red-300' :
    stats.health === 'yellow' ? 'bg-yellow-500/20 text-yellow-300' : 'bg-emerald-500/20 text-emerald-300'
  return (
    <button onClick={onSelect}
      className={`w-full text-left bg-[var(--color-navy-700)] rounded-lg p-2.5 border transition-colors ${
        selected ? 'border-[var(--color-gold)]/40' : 'border-white/5 hover:border-[var(--color-gold)]/20'
      }`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-medium text-[var(--color-ink)] truncate">{stats.name}</div>
          <div className="text-[10px] text-[var(--color-ink-muted)] truncate">{stats.client}</div>
        </div>
        <span className={`text-[10px] px-1.5 py-0.5 rounded ${healthBg} uppercase font-semibold`}>{stats.health}</span>
      </div>
      <div className="mt-2 grid grid-cols-3 gap-1 text-[10px] font-mono">
        <div><span className="text-[var(--color-ink-muted)]">$/hr</span> <span className="text-[var(--color-gold)] font-semibold">{stats.effectiveHourlyRate > 0 ? stats.effectiveHourlyRate.toFixed(0) : '–'}</span></div>
        <div><span className="text-[var(--color-ink-muted)]">hrs</span> <span className="text-[var(--color-ink)]">{stats.actualHours}/{stats.estimatedHours}</span></div>
        <div><span className="text-[var(--color-ink-muted)]">var</span> <span className={stats.hoursVariancePct > 10 ? 'text-red-400' : 'text-[var(--color-ink)]'}>{stats.hoursVariancePct > 0 ? '+' : ''}{stats.hoursVariancePct.toFixed(0)}%</span></div>
      </div>
    </button>
  )
}

function ProjectDetailCard({ project, stats, onStatusChange, onDelete }: {
  project: Project,
  stats: ReturnType<typeof statsForProject>,
  onStatusChange: (s: Project['status']) => void,
  onDelete: () => void,
}) {
  const variance = stats.hoursVariance
  return (
    <div className="bg-[var(--color-navy-800)] rounded-xl border border-white/10 p-4">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h2 className="text-lg font-semibold text-[var(--color-ink)]">{project.name}</h2>
          <p className="text-xs text-[var(--color-ink-muted)]">{project.client} · created {project.createdAt}</p>
        </div>
        <div className="flex items-center gap-1">
          {project.status !== 'archived' && (
            <button onClick={() => onStatusChange(project.status === 'done' ? 'active' : 'done')}
              className="px-2 py-1 text-[10px] rounded bg-[var(--color-navy-700)] hover:bg-[var(--color-navy-600)] text-[var(--color-ink-muted)] hover:text-[var(--color-ink)] uppercase tracking-wider transition-colors">
              {project.status === 'done' ? 'Reopen' : 'Mark done'}
            </button>
          )}
          <button onClick={() => onStatusChange(project.status === 'archived' ? 'active' : 'archived')}
            className="px-2 py-1 text-[10px] rounded bg-[var(--color-navy-700)] hover:bg-[var(--color-navy-600)] text-[var(--color-ink-muted)] hover:text-[var(--color-ink)] transition-colors flex items-center gap-1">
            {project.status === 'archived' ? <ArchiveRestore className="w-3 h-3" /> : <Archive className="w-3 h-3" />}
          </button>
          <button onClick={onDelete}
            className="px-2 py-1 text-[10px] rounded bg-[var(--color-navy-700)] hover:bg-red-500/20 text-[var(--color-ink-muted)] hover:text-red-300 transition-colors">
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-3">
        <Stat label="Effective $/hr" value={stats.effectiveHourlyRate > 0 ? `$${stats.effectiveHourlyRate.toFixed(0)}` : '–'} accent />
        <Stat label="Estimated $/hr" value={`$${stats.estimatedHourlyRate.toFixed(0)}`} />
        <Stat label="Actual / est. hrs" value={`${stats.actualHours} / ${stats.estimatedHours}`} />
        <Stat label="Variance" value={`${variance > 0 ? '+' : ''}${variance.toFixed(1)}h`} />
      </div>

      {stats.health === 'red' && (
        <div className="mt-3 flex items-start gap-2 text-xs text-red-300 bg-red-500/10 rounded-md p-2.5 border border-red-500/20">
          <TrendingDown className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <p>You&apos;re {stats.hoursVariancePct.toFixed(0)}% over the original estimate. Your effective rate has dropped from ${stats.estimatedHourlyRate.toFixed(0)}/hr to ${stats.effectiveHourlyRate.toFixed(0)}/hr. Consider re-scoping or raising the next quote.</p>
        </div>
      )}
      {stats.health === 'yellow' && (
        <div className="mt-3 flex items-start gap-2 text-xs text-yellow-300 bg-yellow-500/10 rounded-md p-2.5 border border-yellow-500/20">
          <TrendingUp className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <p>Getting close to the estimate ({stats.hoursVariancePct > 0 ? '+' : ''}{stats.hoursVariancePct.toFixed(0)}%). Watch for scope creep on the remaining work.</p>
        </div>
      )}
    </div>
  )
}

function NewEntryForm({ projectId, onAdd }: {
  projectId: string, onAdd: (e: Omit<TimeEntry, 'id'>) => void
}) {
  const [date, setDate] = useState(today())
  const [hours, setHours] = useState('')
  const [description, setDescription] = useState('')
  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    const h = parseFloat(hours)
    if (!isFinite(h) || h <= 0) return
    onAdd({ projectId, date, hours: h, description: description.trim() })
    setHours(''); setDescription('')
  }
  return (
    <form onSubmit={submit} className="grid grid-cols-12 gap-2">
      <input type="date" value={date} onChange={e => setDate(e.target.value)}
        className="col-span-3 bg-[var(--color-navy-900)] border border-white/10 rounded-md px-2.5 py-1.5 text-sm font-mono focus:outline-none focus:border-[var(--color-gold)]/50" />
      <input type="number" step="0.25" min="0" placeholder="hours" value={hours} onChange={e => setHours(e.target.value)}
        className="col-span-2 bg-[var(--color-navy-900)] border border-white/10 rounded-md px-2.5 py-1.5 text-sm font-mono placeholder:text-[var(--color-ink-muted)]/60 focus:outline-none focus:border-[var(--color-gold)]/50" />
      <input value={description} onChange={e => setDescription(e.target.value)} placeholder="What did you work on?"
        className="col-span-5 bg-[var(--color-navy-900)] border border-white/10 rounded-md px-2.5 py-1.5 text-sm placeholder:text-[var(--color-ink-muted)]/60 focus:outline-none focus:border-[var(--color-gold)]/50" />
      <button type="submit"
        className="col-span-2 px-2.5 py-1.5 rounded-md bg-[var(--color-gold)] text-[var(--color-navy-900)] text-xs font-semibold hover:bg-[var(--color-gold-bright)] transition-colors flex items-center justify-center gap-1.5">
        <Plus className="w-3.5 h-3.5" /> Log
      </button>
    </form>
  )
}

function EntryList({ entries, onDelete }: { entries: TimeEntry[], onDelete: (id: string) => void }) {
  if (entries.length === 0) {
    return <p className="text-xs text-[var(--color-ink-muted)] italic">No entries yet. Log your first session above.</p>
  }
  return (
    <div className="space-y-1.5 max-h-96 overflow-y-auto">
      {entries.map(e => (
        <div key={e.id} className="group flex items-center gap-3 px-2.5 py-1.5 rounded-md bg-[var(--color-navy-700)] border border-white/5 hover:border-white/15 transition-colors">
          <div className="text-xs font-mono text-[var(--color-ink-muted)] w-24 flex-shrink-0">{e.date}</div>
          <div className="text-sm font-mono text-[var(--color-gold)] w-16 text-right flex-shrink-0">{e.hours.toFixed(2)}h</div>
          <div className="text-sm text-[var(--color-ink)] flex-1 truncate">{e.description || <span className="text-[var(--color-ink-muted)] italic">no description</span>}</div>
          <button onClick={() => onDelete(e.id)}
            className="opacity-0 group-hover:opacity-100 text-[var(--color-ink-muted)] hover:text-red-400 transition-all">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
    </div>
  )
}

function EmptyState() {
  return (
    <div className="bg-[var(--color-navy-800)] rounded-xl border border-white/10 p-12 text-center">
      <Clock className="w-12 h-12 text-[var(--color-gold)]/40 mx-auto mb-3" />
      <h2 className="text-lg font-semibold text-[var(--color-ink)] mb-1">Pick a project to begin</h2>
      <p className="text-sm text-[var(--color-ink-muted)] max-w-md mx-auto">
        Add projects with their quoted price and estimated hours. Log time as you work.
        TimeLedger calculates your real effective rate so you stop underpricing the next quote.
      </p>
    </div>
  )
}

function QuoteCheckModal({ price, setPrice, hours, setHours, result, onClose }: {
  price: string, setPrice: (v: string) => void,
  hours: string, setHours: (v: string) => void,
  result: ReturnType<typeof checkQuote> | null,
  onClose: () => void,
}) {
  const verdictColor =
    result?.recommendation === 'underpriced' ? 'text-red-300 bg-red-500/10 border-red-500/30' :
    result?.recommendation === 'overpriced' ? 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30' :
    'text-[var(--color-ink)] bg-[var(--color-navy-700)] border-white/10'
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div className="relative w-full max-w-lg rounded-xl border border-white/10 bg-[var(--color-navy-800)] p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-[var(--color-ink)] mb-1 flex items-center gap-2">
          <Calculator className="w-4 h-4 text-[var(--color-gold)]" /> Quote sanity check
        </h2>
        <p className="text-xs text-[var(--color-ink-muted)] mb-5">
          Compare a proposed quote against your real blended hourly rate before you send it.
        </p>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--color-ink-muted)] block mb-1">Price ($)</label>
            <input type="number" value={price} onChange={e => setPrice(e.target.value)} placeholder="2500"
              className="w-full bg-[var(--color-navy-900)] border border-white/10 rounded-md px-2.5 py-2 text-sm font-mono focus:outline-none focus:border-[var(--color-gold)]/50" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--color-ink-muted)] block mb-1">Hours</label>
            <input type="number" value={hours} onChange={e => setHours(e.target.value)} placeholder="20"
              className="w-full bg-[var(--color-navy-900)] border border-white/10 rounded-md px-2.5 py-2 text-sm font-mono focus:outline-none focus:border-[var(--color-gold)]/50" />
          </div>
        </div>
        {result && (
          <div className={`rounded-md p-3 border text-sm ${verdictColor}`}>
            <div className="flex items-center gap-2 mb-1 font-semibold uppercase tracking-wider text-xs">
              {result.recommendation === 'underpriced' && <TrendingDown className="w-3.5 h-3.5" />}
              {result.recommendation === 'overpriced' && <TrendingUp className="w-3.5 h-3.5" />}
              {result.recommendation === 'reasonable' && <DollarSign className="w-3.5 h-3.5" />}
              {result.recommendation}
            </div>
            <p className="leading-relaxed">{result.message}</p>
          </div>
        )}
        <div className="flex justify-end mt-4">
          <button onClick={onClose}
            className="px-3 py-1.5 rounded-md bg-[var(--color-navy-700)] hover:bg-[var(--color-navy-600)] text-[var(--color-ink-muted)] hover:text-[var(--color-ink)] text-xs font-medium">
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
